import {
  Alert,
  Badge,
  Card,
  Grid,
  Group,
  Loader,
  Progress,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import type React from 'react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ApiError, api } from '../api.js';

type Overview = Awaited<ReturnType<typeof api.systemOverview>>;
type TierSeries = Awaited<ReturnType<typeof api.tierTimeseries>>;

export function HomePage(): React.JSX.Element {
  const [data, setData] = useState<Overview | null>(null);
  const [tierTrend, setTierTrend] = useState<TierSeries | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    const tick = () => {
      api
        .systemOverview()
        .then((r) => {
          if (alive) setData(r);
        })
        .catch((e) => {
          if (alive) setErr(e instanceof ApiError ? e.message : String(e));
        });
    };
    tick();
    // Tier trend refreshes more slowly — daily bucket data doesn't move
    // minute-to-minute.
    api
      .tierTimeseries(14)
      .then((r) => alive && setTierTrend(r))
      .catch(() => undefined);
    const id = setInterval(tick, 30_000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  if (err) return <Alert color="red">{err}</Alert>;
  if (!data) return <Loader />;

  const fmt$ = (n: number) => `$${n.toFixed(4)}`;
  const monthly = data.spend_usd.monthly_pct * 100;
  const hourly = data.spend_usd.hourly_pct * 100;
  const overBudget = monthly >= 90 || hourly >= 90;

  return (
    <Stack>
      <Group justify="space-between" align="baseline">
        <Title order={3}>Today</Title>
        <Group gap="xs">
          {data.killswitch_on ? (
            <Badge color="red" size="lg">
              KILLSWITCH ON
            </Badge>
          ) : (
            <Badge color="green" size="lg">
              ai actions enabled
            </Badge>
          )}
          {data.shadow_mode && <Badge color="yellow">shadow mode</Badge>}
          {data.instance.cron_enabled && <Badge color="blue">cron runner (this instance)</Badge>}
        </Group>
      </Group>

      {overBudget && (
        <Alert color="orange" title="Budget warning">
          Approaching cap — hourly {hourly.toFixed(0)}%, monthly {monthly.toFixed(0)}%.
        </Alert>
      )}

      <Grid>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <Tile
            label="This hour"
            value={fmt$(data.spend_usd.hourly)}
            sub={`${hourly.toFixed(1)}% of $${data.caps.hourly_usd}`}
            color={hourly > 75 ? 'red' : hourly > 50 ? 'orange' : 'green'}
            to="/observability/spend"
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <Tile
            label="Last 24h"
            value={fmt$(data.spend_usd.today)}
            sub="LLM spend"
            color="blue"
            to="/observability/spend"
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <Tile
            label="This month"
            value={fmt$(data.spend_usd.monthly)}
            sub={`${monthly.toFixed(1)}% of $${data.caps.monthly_usd}`}
            color={monthly > 75 ? 'red' : monthly > 50 ? 'orange' : 'green'}
            to="/observability/spend"
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <Tile
            label="Total messages"
            value={data.messages_total.toLocaleString()}
            sub="indexed in DB"
            color="violet"
            to="/accounts"
          />
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <Tile
            label="Classified (24h)"
            value={data.classified_24h.toLocaleString()}
            sub={`${data.pending_24h} pending`}
            color="green"
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <Tile
            label="Pending review"
            value={String(data.queues.drafts_review_pending)}
            sub="drafts awaiting approval"
            color={data.queues.drafts_review_pending > 0 ? 'yellow' : 'gray'}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <Tile
            label="Proposed rules"
            value={String(data.queues.proposed_rules_pending)}
            sub="from your feedback — confirm/reject"
            color={data.queues.proposed_rules_pending > 0 ? 'yellow' : 'gray'}
            to="/admin/proposed-rules"
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <Tile
            label="IMAP IDLE"
            value={data.instance.imap_idle ? 'on' : 'off'}
            sub={`port ${data.instance.port} · pid ${data.instance.pid}`}
            color={data.instance.imap_idle ? 'green' : 'gray'}
          />
        </Grid.Col>
      </Grid>

      <Card padding="md" withBorder>
        <Title order={5} mb="sm">
          Last 24h by tier
        </Title>
        <TierBreakdown rows={data.by_tier_24h} />
      </Card>

      {tierTrend && tierTrend.rows.length > 0 && (
        <Card padding="md" withBorder>
          <Title order={5} mb="sm">
            Tier distribution over last {tierTrend.days} days
          </Title>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={pivotTierByDay(tierTrend.rows)}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis dataKey="day" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="P0" stackId="t" fill="#fa5252" />
              <Bar dataKey="P1" stackId="t" fill="#fd7e14" />
              <Bar dataKey="P2" stackId="t" fill="#fab005" />
              <Bar dataKey="P3" stackId="t" fill="#228be6" />
              <Bar dataKey="P4" stackId="t" fill="#868e96" />
              <Bar dataKey="FYI" stackId="t" fill="#adb5bd" />
              <Bar dataKey="Newsletter" stackId="t" fill="#ced4da" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}
    </Stack>
  );
}

function Tile({
  label,
  value,
  sub,
  color,
  to,
}: {
  label: string;
  value: string;
  sub: string;
  color: string;
  to?: string;
}): React.JSX.Element {
  const content = (
    <Card padding="md" withBorder style={to ? { cursor: 'pointer' } : undefined}>
      <Stack gap={4}>
        <Text size="xs" c="dimmed" tt="uppercase">
          {label}
        </Text>
        <Text size="xl" fw={600} c={color}>
          {value}
        </Text>
        <Text size="xs" c="dimmed">
          {sub}
        </Text>
      </Stack>
    </Card>
  );
  if (to) {
    return (
      <Link to={to} style={{ textDecoration: 'none', color: 'inherit' }}>
        {content}
      </Link>
    );
  }
  return content;
}

const TIER_COLOR: Record<string, string> = {
  P0: 'red',
  P1: 'orange',
  P2: 'yellow',
  P3: 'blue',
  P4: 'gray',
  FYI: 'gray',
  Newsletter: 'gray',
};

const CANONICAL = new Set(['P0', 'P1', 'P2', 'P3', 'P4', 'FYI', 'Newsletter']);

// Round 25 R25-1: pivot the long-form (day, tier, n) rows into wide-form
// rows ({day, P0, P1, P2, ...}) which is what recharts BarChart expects
// for stacked bars.
function pivotTierByDay(
  rows: Array<{ day: string; tier: string; n: number }>,
): Array<Record<string, string | number>> {
  const byDay = new Map<string, Record<string, number>>();
  for (const r of rows) {
    if (!CANONICAL.has(r.tier)) continue;
    const slot = byDay.get(r.day) ?? {};
    slot[r.tier] = (slot[r.tier] ?? 0) + r.n;
    byDay.set(r.day, slot);
  }
  return [...byDay.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([day, t]) => ({ day: day.slice(5), ...t }));
}

// Round 23 R23-6: collapse all non-canonical tier values into a single
// "other" bucket with a warning badge. The worker fix (R23-1) prevents
// new drift; this keeps the chart readable for any legacy / pre-fix rows.
function TierBreakdown({
  rows,
}: {
  rows: Array<{ tier: string; n: number }>;
}): React.JSX.Element {
  if (rows.length === 0) {
    return (
      <Text c="dimmed" size="sm">
        No classifications in the last 24h.
      </Text>
    );
  }
  const order = ['P0', 'P1', 'P2', 'P3', 'P4', 'FYI', 'Newsletter'];
  const buckets = new Map<string, number>();
  let drift = 0;
  for (const r of rows) {
    if (CANONICAL.has(r.tier)) buckets.set(r.tier, (buckets.get(r.tier) ?? 0) + r.n);
    else drift += r.n;
  }
  const display: Array<{ tier: string; n: number }> = order
    .filter((t) => buckets.has(t))
    .map((t) => ({ tier: t, n: buckets.get(t) ?? 0 }));
  if (drift > 0) display.push({ tier: 'other (non-canonical)', n: drift });
  const max = Math.max(...display.map((x) => x.n), 1);
  return (
    <Stack gap="xs">
      {display.map((t) => {
        const pct = (t.n / max) * 100;
        const isOther = !CANONICAL.has(t.tier.split(' ')[0] ?? '');
        const color = isOther ? 'yellow' : TIER_COLOR[t.tier] ?? 'gray';
        return (
          <Group key={t.tier} gap="sm" wrap="nowrap">
            <Text size="sm" w={180}>
              <Badge color={color} variant="light" size="sm">
                {t.tier}
              </Badge>
            </Text>
            <Progress value={pct} color={color} flex={1} />
            <Text size="sm" w={60} ta="right">
              {t.n.toLocaleString()}
            </Text>
          </Group>
        );
      })}
    </Stack>
  );
}
